import { useEffect, useMemo, useState } from 'react';
import { CalendarDays } from 'lucide-react';

function diff(target, now) {
  const ms = target.getTime() - now.getTime();
  if (ms <= 0) return null;
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  return { days, hours, minutes };
}

function pickNextOpener(openers) {
  if (!Array.isArray(openers) || openers.length === 0) return null;
  const now = new Date();
  const upcoming = openers
    .map((o) => {
      const label = (o?.label || '').trim();
      const date = (o?.date || '').trim();
      if (!label || !date) return null;
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      if (d.getTime() < now.getTime()) return null;
      return { label, date: d };
    })
    .filter(Boolean)
    .sort((a, b) => a.date - b.date);
  return upcoming[0] || null;
}

function formatLongDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function SeasonCountdown({ openers, testId = 'season-countdown' }) {
  const next = useMemo(() => pickNextOpener(openers), [openers]);
  const [remaining, setRemaining] = useState(() => (next ? diff(next.date, new Date()) : null));

  useEffect(() => {
    if (!next) return undefined;
    setRemaining(diff(next.date, new Date()));
    const t = setInterval(() => {
      setRemaining(diff(next.date, new Date()));
    }, 30 * 1000); // every 30s is smooth enough
    return () => clearInterval(t);
  }, [next]);

  if (!next || !remaining) return null;

  const parts = [
    { value: remaining.days, label: remaining.days === 1 ? 'day' : 'days' },
    { value: remaining.hours, label: remaining.hours === 1 ? 'hour' : 'hours' },
    { value: remaining.minutes, label: remaining.minutes === 1 ? 'min' : 'mins' },
  ];

  return (
    <aside
      data-testid={testId}
      aria-label={`Countdown to ${next.label}`}
      className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] card-elev"
    >
      <div className="grid gap-4 p-5 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-6 sm:p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]">
            <CalendarDays size={20} />
          </span>
          <div>
            <p className="kicker">Season pulse</p>
            <p className="font-serif text-xl leading-tight tracking-tight">
              {next.label}
            </p>
          </div>
        </div>
        <ul
          className="flex items-baseline gap-4 sm:gap-6 sm:justify-center"
          data-testid={`${testId}-values`}
        >
          {parts.map((p) => (
            <li key={p.label} className="flex items-baseline gap-1.5">
              <span className="font-serif text-3xl sm:text-4xl tabular-nums text-[hsl(var(--foreground))]">
                {p.value}
              </span>
              <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">
                {p.label}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-[hsl(var(--muted-foreground))] sm:text-right">
          {formatLongDate(next.date)}
        </p>
      </div>
    </aside>
  );
}
